const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// F14 — CSRF token cache. Lo cargamos vagamente y lo renovamos si el servidor
// responde 403 con código CSRF (lo identificamos por el código de error de
// @fastify/csrf-protection: EBADCSRFTOKEN).
let csrfTokenCache: string | null = null
let csrfTokenPromise: Promise<string> | null = null

async function fetchCsrfToken(): Promise<string> {
  const res = await fetch(`${API_BASE}/csrf-token`, { credentials: 'include' })
  if (!res.ok) throw new Error(`No se pudo obtener token CSRF (${res.status})`)
  const { token } = (await res.json()) as { token: string }
  csrfTokenCache = token
  return token
}

async function ensureCsrfToken(): Promise<string> {
  if (csrfTokenCache) return csrfTokenCache
  if (!csrfTokenPromise) {
    csrfTokenPromise = fetchCsrfToken().finally(() => {
      csrfTokenPromise = null
    })
  }
  return csrfTokenPromise
}

// F14 — Sesiones server-side: si cualquier llamada (salvo el propio login,
// donde un 401 significa "credenciales incorrectas" o "falta MFA") devuelve
// 401, la sesión murió (timeout idle/absoluto o revocación). Avisamos al
// AuthContext para que pase a 'unauthenticated' y AuthGuard redirija a /login.
let unauthorizedHandler: (() => void) | null = null

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler
}

/** Notifica un 401 del servidor (sesión caducada/revocada). Para los fetch
 * que no pasan por `request` (descargas, streaming SSE). */
export function notifyUnauthorized(): void {
  unauthorizedHandler?.()
}

function maybeNotifyUnauthorized(path: string, status: number): void {
  if (status === 401 && path !== '/auth/login') notifyUnauthorized()
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()

  const headers = new Headers(init.headers ?? {})
  // No forzamos Content-Type para FormData (multipart): el browser lo añade con boundary.
  const isFormData = init.body instanceof FormData
  if (!isFormData && !headers.has('Content-Type') && init.body !== undefined) {
    headers.set('Content-Type', 'application/json')
  }
  if (!SAFE_METHODS.has(method)) {
    const token = await ensureCsrfToken()
    headers.set('X-CSRF-Token', token)
  }

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers,
  })

  // Si el servidor rechaza por CSRF (token caducó/cookie rotó), reintentamos UNA vez.
  if (res.status === 403 && !SAFE_METHODS.has(method)) {
    let body: unknown
    try {
      body = await res.clone().json()
    } catch {
      body = null
    }
    const code = (body as { code?: string } | null)?.code ?? ''
    if (code === 'EBADCSRFTOKEN' || code === 'FST_CSRF_INVALID_TOKEN') {
      csrfTokenCache = null
      const token = await ensureCsrfToken()
      headers.set('X-CSRF-Token', token)
      const retry = await fetch(`${API_BASE}${path}`, {
        credentials: 'include',
        ...init,
        headers,
      })
      maybeNotifyUnauthorized(path, retry.status)
      return await parseResponse<T>(retry)
    }
  }

  maybeNotifyUnauthorized(path, res.status)
  return await parseResponse<T>(res)
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      body = await res.text()
    }
    throw new ApiError(`API ${res.status} ${res.statusText}`, res.status, body)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /**
   * Subida multipart (FormData). Pasa por `request`, que omite Content-Type
   * (el navegador pone el boundary), incluye la cookie y añade X-CSRF-Token
   * con reintento si el token caducó. Úsalo para uploads en vez de fetch crudo.
   */
  upload: <T>(path: string, form: FormData, method: 'POST' | 'PUT' = 'POST') =>
    request<T>(path, { method, body: form }),
}

/**
 * Devuelve el token CSRF vigente (cargándolo si hace falta). Para casos que no
 * pueden usar `request`, como el fetch de streaming SSE de /ai/chat.
 */
export function getCsrfToken(): Promise<string> {
  return ensureCsrfToken()
}

/** Forzar un refresh del token (tras login/logout, por si la cookie cambió). */
export function resetCsrfToken(): void {
  csrfTokenCache = null
}
